import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  CreditCard,
  BookOpen,
  Layers,
  Building2,
  GraduationCap,
  HeartHandshake,
  CheckCircle2,
} from "lucide-react";

const whatWeDo = [
  {
    icon: CreditCard,
    title: "Rewards-Based Student Banking",
    description:
      "A points-based banking experience designed to teach saving, earning and responsible spending — built for K-12 learners.",
  },
  {
    icon: BookOpen,
    title: "Age-Based Literacy Programs",
    description:
      "Curriculum-aligned financial literacy modules that evolve with students from early primary through secondary school.",
  },
  {
    icon: Layers,
    title: "Hybrid Learning Model",
    description:
      "A blended delivery that combines digital simulations, classroom workshops and real-world practice.",
  },
  {
    icon: Sparkles,
    title: "SaaS for Institutions",
    description:
      "A turnkey platform for schools, institutions and CSR partners to deploy financial literacy at scale.",
  },
];

const whyItMatters = [
  "Early exposure builds lifelong financial habits and confidence.",
  "Structured curriculum bridges classroom learning and real-world money decisions.",
  "Technology makes concepts interactive, measurable and genuinely engaging.",
  "Institutions gain a scalable, future-ready literacy program their communities value.",
];

const partners = [
  {
    icon: GraduationCap,
    title: "Schools & Institutions",
    description:
      "Partner with us to embed financial literacy as a core outcome of your curriculum.",
  },
  {
    icon: HeartHandshake,
    title: "CSR Foundations",
    description:
      "Fund and scale programs that deliver measurable financial-education impact across communities.",
  },
  {
    icon: Building2,
    title: "Education Partners",
    description:
      "Co-build age-appropriate modules, certifications and learning journeys for your learners.",
  },
];

export const HomePage = () => {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-white"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-slate-200/60 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-20 md:pb-32">
          <div className="max-w-3xl reveal">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-5">
              EdTech × FinTech — India
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Empowering{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Financially</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-amber-500/40 -z-0 rounded-sm" />
              </span>{" "}
              Smart Students.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Building a new-age financial literacy ecosystem for K-12 students
              through technology, innovation and real-world learning — one
              student at a time.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white px-6 py-3.5 text-base font-semibold hover:bg-slate-800 transition-colors"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-learn-more"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-slate-900 border border-slate-200 px-6 py-3.5 text-base font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                Learn More
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              <span>K-12 focused</span>
              <span className="text-slate-300">•</span>
              <span>Rewards-based</span>
              <span className="text-slate-300">•</span>
              <span>SaaS for schools</span>
              <span className="text-slate-300">•</span>
              <span>Hybrid model</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SNAPSHOT */}
      <section
        data-testid="about-snapshot-section"
        className="py-20 md:py-28 bg-slate-50 border-y border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-4">
                Who We Are
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                A financial literacy company, purpose-built for the next
                generation.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3">
              <p className="text-lg leading-relaxed text-slate-600">
                <span className="font-semibold text-slate-900">
                  SparkNext Pathways Private Limited
                </span>{" "}
                is an EdTech + FinTech company focused on enabling financial
                literacy and responsible money habits among students through
                innovative digital solutions. We bridge the gap between
                classroom education and real-world financial skills — building
                products that schools, institutions and families can trust.
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6">
                <Stat label="K-12 focus" value="Age-based" />
                <Stat label="Delivery" value="Hybrid" />
                <Stat label="Model" value="SaaS" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section
        data-testid="what-we-do-section"
        className="py-20 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-4">
              What We Do
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Four pillars that power financial confidence in students.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whatWeDo.map((item, idx) => (
              <div
                key={item.title}
                data-testid={`what-we-do-card-${idx}`}
                className="group bg-white border border-slate-200 rounded-xl p-7 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-5 group-hover:bg-amber-500 group-hover:text-slate-900 transition-colors">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section
        data-testid="why-it-matters-section"
        className="py-20 md:py-32 bg-slate-900 text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400 mb-4">
                Why It Matters
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                In today's world, financial literacy is no longer optional.
              </h2>
              <p className="mt-5 text-slate-300 leading-relaxed text-base">
                Introducing structured financial education at the K-12 level
                helps students develop responsible financial habits early — and
                carry them into adulthood.
              </p>
            </div>

            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {whyItMatters.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-4 border-t border-slate-800 pt-5 first:border-t-0 first:pt-0"
                  >
                    <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="text-base text-slate-200 leading-relaxed">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS */}
      <section
        data-testid="partnerships-section"
        className="py-20 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-4">
              Partnerships
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Scaling financial literacy, together.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              We welcome partnerships with institutions and communities that
              share our belief: every student deserves the foundation to make
              smart financial decisions.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {partners.map((p, idx) => (
              <div
                key={p.title}
                data-testid={`partnership-card-${idx}`}
                className="bg-slate-50 border border-slate-200 rounded-xl p-7 hover:bg-white hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 rounded-lg bg-amber-500 text-slate-900 flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {p.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="cta-banner-section"
        className="bg-slate-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400 mb-4">
                Let's Build Together
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Partner with us to shape financially smart students.
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="home-cta-partner"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 text-slate-900 px-6 py-3.5 text-base font-semibold hover:bg-amber-400 transition-colors"
              >
                Partner With Us
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-get-in-touch"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-transparent text-white border border-slate-700 hover:border-slate-500 px-6 py-3.5 text-base font-semibold transition-colors"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
      {label}
    </p>
    <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">
      {value}
    </p>
  </div>
);

export default HomePage;
